import { displayLocale } from './localeState'
import { uiText } from './uiText'
import english from './runtime-en.json'

/** Display only. Never use this output to derive task state, authorization or API values. */
export function systemMessage(value: string | null | undefined): string {
  const text = value ?? ''
  if (displayLocale.value !== 'en-US' || !text || text.length > 16_384) return text
  return translate(text, 0)
}

function exact(text: string): string {
  return Object.hasOwn(english, text) ? (english as Record<string, string>)[text]! : uiText(text)
}

function translate(text: string, depth: number): string {
  if (depth > 3) return text
  const known = exact(text)
  if (known !== text) return known
  // Only complete, reviewed message formats. Captures are copied literally,
  // never reinterpreted as HTML, commands, status codes or authorization.
  const http = /^核验接口返回 HTTP (\d{3})，未取得服务端任务核验授权，未写入密码$/.exec(text)
  if (http) return `Authorization endpoint returned HTTP ${http[1]}; server authorization was not obtained and the password was not written`
  const tool = /^自动改密工具拒绝当前账号或密码记录格式（代码 (\d+)）$/.exec(text)
  if (tool) return `The automatic reset tool rejected the account or password record format (code ${tool[1]})`
  const targets = /^发现 (\d+) 个可识别 Windows 系统；为避免改错账号，请另行启动 Windows 救援确认目标$/.exec(text)
  if (targets) return `Found ${targets[1]} recognizable Windows systems; start a separate Windows rescue task to confirm the target and avoid resetting the wrong account`
  const smb = /^SMB 挂载失败：([^\r\n；]+)；系统错误码=(-?\d+)；命令退出码=(-?\d+)；timeout=(True|False|true|false)；share=([^\r\n；]+)；drive=([^\r\n；]+)$/.exec(text)
  if (smb) return `SMB mount failed: ${exact(smb[1]!)}; system error code=${smb[2]}; command exit code=${smb[3]}; timeout=${smb[4]}; share=${smb[5]}; drive=${smb[6]}`
  const cause = /^原因[：:]\s*([A-Z][A-Z0-9_]*(?:\s*\/\s*[A-Z0-9_]+)*)$/.exec(text)
  if (cause) return `Reason: ${cause[1]}`
  const driverCounts = /^本轮已结束：全部网卡覆盖 (\d+) 项，主用网卡就绪但有其他网卡提醒 (\d+) 项，待核实 (\d+) 项；未进行实机安装$/.exec(text)
  if (driverCounts) return `Round finished: all NICs covered in ${driverCounts[1]} items; primary NIC ready with other NIC warnings in ${driverCounts[2]}; ${driverCounts[3]} pending verification. No real installation was performed.`
  const coverageCounts = /^本轮已结束：覆盖核查通过 (\d+) 项，未解决 (\d+) 项，共 (\d+) 项；仅表示安装材料覆盖情况，未进行实机安装$/.exec(text)
  if (coverageCounts) return `Round finished: ${coverageCounts[1]} coverage checks passed; ${coverageCounts[2]} unresolved; ${coverageCounts[3]} total. Installation materials were checked; no real installation was performed.`
  const pci = /^(已核对覆盖|仍缺|其他网卡仍缺兼容驱动)：((?:PCI\\VEN_[0-9A-F]{4}&DEV_[0-9A-F]{4}(?:&[A-Z0-9_]+)*)(?:[、, ]+PCI\\VEN_[0-9A-F]{4}&DEV_[0-9A-F]{4}(?:&[A-Z0-9_]+)*)*)$/.exec(text)
  if (pci) return `${pci[1] === '已核对覆盖' ? 'Verified coverage' : pci[1] === '仍缺' ? 'Still missing' : 'Compatible drivers missing for other NICs'}: ${pci[2]}`
  const switchFailure = /^交换机 ([^\r\n，]+)，端口 ([A-Za-z0-9/_.:-]+)：([^\r\n]+)$/.exec(text)
  if (switchFailure) return `Switch ${switchFailure[1]}, port ${switchFailure[2]}: ${translate(switchFailure[3]!, depth + 1)}`
  const prefixes: Record<string, string> = {
    'Windows 自动改密停止：': 'Automatic Windows password reset stopped: ',
    'PE 退出未完成：': 'PE exit incomplete: ',
    'PE 退出准备失败：': 'PE exit preparation failed: ',
    '救援环境与 PXE/DHCP 仍保持运行：': 'The rescue environment and PXE/DHCP remain running: ',
    '交换机操作失败：': 'Switch operation failed: ',
    '保存前检查或备份未通过，未执行保存：': 'Pre-save check or backup failed; configuration was not saved: ',
    '未找到确认适配的官方驱动：': 'No verified compatible official driver found: ',
  }
  for (const [prefix, translated] of Object.entries(prefixes)) {
    if (text.startsWith(prefix) && text.length > prefix.length) return translated + translate(text.slice(prefix.length), depth + 1)
  }
  // Compose recognized server clauses without translating arbitrary substrings.
  // Unknown clauses, including vendor output, retain their exact contents.
  const parts = text.split(/([；。\n])/)
  if (parts.length === 1) return text
  let changed = false
  const translated = parts.map((part, i) => {
    if (i % 2) return part
    const output = translate(part, depth + 1)
    if (output !== part) changed = true
    return output
  })
  return changed ? translated.map((part, i) => i % 2 ? part === '；' ? '; ' : part === '。' ? '. ' : part : part).join('') : text
}
